import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import type { MiningUploadResponseDto } from '@poker-stats/contracts';
import type { RootState } from '../../configureStore';
import type { ApiFailure } from '../../api/types';
import apiService from '../../api/apiServices/apiService';
import i18n from '../../../i18n';
import { finishMiningUpload, setMiningItemStatus, startMiningUpload } from './reducer';

function uploadError(error?: ApiFailure): string {
  if (error?.code === 'NETWORK_ERROR') return i18n.t('mining_upload_network_error');
  if (error?.code === 'INVALID_RESPONSE') return i18n.t('mining_upload_invalid_response');
  if (Array.isArray(error?.message)) return error.message.join('; ');
  if (error?.message) return error.message;
  if (error?.statusCode) {
    return i18n.t('mining_upload_http_error', { status: error.statusCode });
  }
  return i18n.t('mining_upload_failed');
}

function* uploadBatch(action: ReturnType<typeof startMiningUpload>): SagaIterator {
  const { queueId, batchId, room, upload } = action.payload;
  const isCurrent: boolean = yield select(
    (state: RootState) =>
      state.miningUpload.queueId === queueId && state.miningUpload.batchId === batchId,
  );
  if (!isCurrent) return;

  try {
    for (const { id, file } of upload.files) {
      if (upload.signal.aborted) return;
      yield put(setMiningItemStatus({ queueId, batchId, id, status: 'uploading' }));
      try {
        const result: {
          success?: { payload: MiningUploadResponseDto };
          reject?: { payload?: ApiFailure };
        } = yield call([apiService, apiService.uploadMiningFile], room, file, upload.signal);
        if (upload.signal.aborted) return;
        if (result.success?.payload?.status === 'queued') {
          yield put(setMiningItemStatus({ queueId, batchId, id, status: 'uploaded' }));
        } else {
          yield put(
            setMiningItemStatus({
              queueId,
              batchId,
              id,
              status: 'error',
              error: uploadError(
                result.reject?.payload ?? { statusCode: 0, code: 'INVALID_RESPONSE' },
              ),
            }),
          );
        }
      } catch {
        if (upload.signal.aborted) return;
        yield put(
          setMiningItemStatus({
            queueId,
            batchId,
            id,
            status: 'error',
            error: uploadError(),
          }),
        );
      }
    }
  } finally {
    yield put(finishMiningUpload({ queueId, batchId }));
  }
}

export default function* miningUploadSaga() {
  yield takeEvery(startMiningUpload.type, uploadBatch);
}
